/**
 * CategorySelect — Reusable category picker for every document/message editor.
 *
 * Behavior:
 *  - Loads existing categories from `/api/templates/categories` on mount
 *    (de-duped server-side, mixed with sensible HV defaults).
 *  - Always shows the *current* value even if it's not in the list (legacy data).
 *  - Inline „+ Neue Kategorie…" toggles a free-text input so the user can add
 *    a new one without leaving the form.
 *
 * Used in: DocumentTemplateEditor, ContractTemplateEditor, MessagingDialog.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ADD_NEW = "__add_new__";

export default function CategorySelect({ value, onChange, testid = "category-select", className = "" }) {
  const [options, setOptions] = useState([]);
  const [addingNew, setAddingNew] = useState(false);
  const [newValue, setNewValue] = useState("");

  useEffect(() => {
    api.get("/templates/categories")
      .then(({ data }) => setOptions(Array.isArray(data) ? data : []))
      .catch(() => setOptions([]));
  }, []);

  const items = useMemo(() => {
    const set = new Set(options);
    if (value && value.trim()) set.add(value.trim());
    return Array.from(set).sort((a, b) => a.localeCompare(b, "de"));
  }, [options, value]);

  if (addingNew) {
    return (
      <div className={`flex gap-1 ${className}`}>
        <Input
          autoFocus
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder="Neue Kategorie…"
          className="h-8 text-sm"
          data-testid={`${testid}-new`}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              const v = newValue.trim();
              if (v) { onChange(v); setAddingNew(false); setNewValue(""); }
            }
            if (e.key === "Escape") { setAddingNew(false); setNewValue(""); }
          }}
        />
        <button
          type="button"
          onClick={() => { setAddingNew(false); setNewValue(""); }}
          className="text-xs text-slate-500 hover:text-slate-800 px-1"
          data-testid={`${testid}-new-cancel`}
        >
          ×
        </button>
      </div>
    );
  }

  return (
    <Select
      value={value || ""}
      onValueChange={(v) => {
        if (v === ADD_NEW) {
          setAddingNew(true); setNewValue("");
        } else {
          onChange(v);
        }
      }}
    >
      <SelectTrigger className={`h-8 text-sm ${className}`} data-testid={testid}>
        <SelectValue placeholder="Kategorie wählen…" />
      </SelectTrigger>
      <SelectContent>
        {items.map((c) => (
          <SelectItem key={c} value={c}>{c}</SelectItem>
        ))}
        <div className="my-1 border-t border-slate-200" />
        <SelectItem value={ADD_NEW} className="text-emerald-700 font-medium">
          + Neue Kategorie…
        </SelectItem>
      </SelectContent>
    </Select>
  );
}
